// Dimensions for layout selection that should be documented in the layout list:
// - min and max number of cards
// - support for card title/ preamble/ description (yes/ no)
// - long vs. short description
// - long vs. short title
// - image with intricate details (should be larger) vs. image with simple details (can be smaller)
// - asymmetric vs. symmetric layout
// - left vs. right alignment
// - top vs. bottom alignment
// - centered vs. off-center alignment
// - diagonal alignment
// - a lot of whitespace vs. very dense
// - cards stacked (slides/ stack/ reveals/ hover effects) vs. cards spread out
// - single column vs. multi-column
// - equal vs. unequal column widths
// - full-width cards vs. partial-width cards
// - grid-based vs. free form layouts 
// - wing-like or unique shape arrangements
// - split-screen layouts
// - suitable for certain image aspect ratio: mixed / square / portrait / landscape / ...
// - number of tracks covered

// TODO: For story layouts where the order of fields in the DOM is different to the visual order in the GRDI:
// define the visual order of text-fields + entry previews for correct order when pressing "tab" key

// TODO: LAYOUT CONSTRAINTS for layout selection/ suggestion/ filtering algorithm
// List of layout constraints:
// - Visibility of fields, e.g. hide_entry_preview_description
// - Font-size of body/ headings/ ... --> Does this font-size work with the narrow columns of a particular layout?
// - Length of texts
// - Variance in length of texts
// - Number of visible fields
// - Size of images
// - Aspect Ratio of images
// - Images that have "crop" disabled (preserve aspect ratio)
// - Do texts have markdown links?
// - What's the previous / next layout? e.g. is the previous only a Heading?
// - What's the overall usage of layouts on the page? 
//   - Which are the most common? 
//   - Are mostly layouts with inner track coverage selected or extended?
// - Do images have transparency? e.g. logos / graphics
// - Do images have intricate details or are they simple?
// - Does the block has an accent color?
// - ...

// TODO: Define tab key order for layouts if the visual order deviates from the field order

// TODO: store utility classes for e.g. alignment, spacing, etc. for better code reuse per layout

/**
 * Layout group definitions for categorizing and styling layouts
 * @type {Object.<string, {name: string, icon: string, description?: string, default_block_spacing?: string, disabled_spacing?: string[], orientation?: 'horizontal' | 'vertical'}>}
 */
export const layout_groups = {
  'single-column': { 
    name: 'Single Column', 
    icon: 'layout-single',
    description: 'Simple single-column layouts',
    default_block_spacing: 'm',
    disabled_spacing: ['0'], // Text would touch top/bottom edge
    orientation: 'vertical' // Entry previews stack vertically
  },
  'two-column': { 
    name: 'Two Column', 
    icon: 'layout-split',
    description: 'Asymmetric two-column layouts',
    default_block_spacing: 'l'
    // Some layouts allow '0' (full-height), others don't - handled per-layout
    // Orientation varies per-layout - some horizontal, some vertical
  },
  'grid': { 
    name: 'Grid', 
    icon: 'grid',
    description: 'Equalised grid layouts with configurable columns',
    default_block_spacing: 's',
    disabled_spacing: ['0'], // Text would touch top/bottom edge
    orientation: 'horizontal' // Entry previews flow horizontally
  },
  'cover': { 
    name: 'Cover / Hero', 
    icon: 'image',
    description: 'Full-screen image with text overlay',
    default_block_spacing: '0',
    orientation: 'vertical',
    fullscreen_previews: true, // Entry previews cover the entire block
    auto_advance: true // Entry previews are a carousel that plays on its own
  },
  'bento': { 
    name: 'Bento', 
    icon: 'layout-bento',
    description: 'Asymmetric bento-style layouts',
    default_block_spacing: 's',
    disabled_spacing: ['0'] // Text would touch top/bottom edge
    // Orientation varies per-layout
  },
  'editorial': { 
    name: 'Editorial', 
    icon: 'layout-editorial',
    description: 'Magazine-style editorial layouts',
    default_block_spacing: 'l',
    disabled_spacing: ['0'], // Text would touch top/bottom edge
    orientation: 'vertical'
  },
  'special': {
    name: 'Special',
    icon: 'sparkle',
    description: 'Unique interactive layouts',
    default_block_spacing: 'm'
    // Orientation varies per-layout
  },
};

/**
 * The layouts arrow-key cycling steps through: one per shape, skipping the width variants.
 *
 * Width is a block property now and has its own drag handle, so cycling through every id
 * meant pressing the arrow key four or five times to get past a single design - 131 presses
 * to get round the catalogue instead of roughly 40.
 */
let cycleable_layouts_cache = null;
function get_cycleable_layouts() {
  if (cycleable_layouts_cache) return cycleable_layouts_cache;
  const seen = new Set();
  cycleable_layouts_cache = story_layouts.filter(layout => {
    // Layouts whose width lives on the block collapse to one entry per shape
    if (!supports_independent_track(layout)) return true;
    const shape = `${layout.group}|${layout.columns ?? ''}|${layout.alignment ?? ''}|${layout.title ?? ''}`;
    if (seen.has(shape)) return false;
    seen.add(shape);
    return true;
  });
  return cycleable_layouts_cache;
}

/**
 * Cycles through the story layouts in the specified direction, one shape at a time.
 * @param {number|string} current_layout - The current layout value.
 * @param {'next'|'previous'} direction - The direction to cycle (either 'next' or 'previous').
 * @returns {number} The value of the next layout in the cycle.
 */
export function cycle_layout(current_layout, direction) {
  const layouts = get_cycleable_layouts();
  const current = get_layout(current_layout);

  // The current layout may be a width variant that is not itself in the list, so fall back
  // to matching its shape - otherwise cycling would always restart from the beginning.
  let current_index = layouts.findIndex((layout) => layout.value === current_layout);
  if (current_index === -1 && current) {
    current_index = layouts.findIndex((layout) =>
      layout.group === current.group &&
      layout.columns === current.columns &&
      layout.alignment === current.alignment &&
      layout.title === current.title
    );
  }

  if (current_index === -1) {
    return direction === 'next' ? layouts[0].value : layouts[layouts.length - 1].value;
  }

  const next_index = direction === 'next'
    ? (current_index + 1) % layouts.length
    : (current_index - 1 + layouts.length) % layouts.length;

  return layouts[next_index].value;
}

/**
 * Get layout by value
 * @param {number} value - The layout value/ID
 * @returns {Object|undefined} The layout object or undefined
 */
export function get_layout(value) {
  return story_layouts.find(layout => layout.value === value);
}

/**
 * Get all layouts in a specific group
 * @param {string} group - The group name
 * @returns {Array} Array of layouts in that group
 */
export function get_layouts_by_group(group) {
  return story_layouts.filter(layout => layout.group === group);
}

/**
 * Get the default block spacing for a layout
 * Falls back to group default, then to 'm'
 * @param {Object|number} layout - The layout object or layout value/ID
 * @returns {string} The default block spacing ('0', 'xs', 's', 'm', 'l')
 */
export function get_default_block_spacing(layout) {
  const layout_obj = typeof layout === 'number' ? get_layout(layout) : layout;
  if (!layout_obj) return 'm';
  
  // Layout-specific override takes priority
  if (layout_obj.default_block_spacing) {
    return layout_obj.default_block_spacing;
  }
  
  // Fall back to group default
  const group = layout_groups[layout_obj.group];
  if (group?.default_block_spacing) {
    return group.default_block_spacing;
  }
  
  return 'm'; // Ultimate fallback
}

/**
 * Get the orientation for a layout (horizontal or vertical)
 * Used to determine move left/right vs move up/down for entry previews
 * Falls back to group default, then to 'vertical'
 * @param {Object|number} layout - The layout object or layout value/ID
 * @returns {'horizontal' | 'vertical'} The layout orientation
 */
export function get_layout_orientation(layout) {
  const layout_obj = typeof layout === 'number' ? get_layout(layout) : layout;
  if (!layout_obj) return 'vertical';
  
  // Layout-specific override takes priority
  if (layout_obj.orientation) {
    return layout_obj.orientation;
  }
  
  // Fall back to group default
  const group = layout_groups[layout_obj.group];
  if (group?.orientation) {
    return group.orientation;
  }
  
  return 'vertical'; // Ultimate fallback
}

/**
 * Check if a layout has fullscreen entry previews
 * Fullscreen layouts have entry previews that cover the entire block,
 * requiring special toolbar handling to avoid overlap
 * @param {Object|number} layout - The layout object or layout value/ID
 * @returns {boolean} True if entry previews cover the entire block
 */
export function has_fullscreen_previews(layout) {
  const layout_obj = typeof layout === 'number' ? get_layout(layout) : layout;
  if (!layout_obj) return false;
  
  // Layout-specific override takes priority
  if (layout_obj.fullscreen_previews !== undefined) {
    return layout_obj.fullscreen_previews;
  }
  
  // Fall back to group default
  const group = layout_groups[layout_obj.group];
  return group?.fullscreen_previews || false;
}

/**
 * Check if a layout's entry previews are a scroll-snap carousel that advances on its own.
 * One flag for every such layout, so they all run the same carousel_auto_advance action.
 * @param {Object|number} layout - The layout object or layout value/ID
 * @returns {boolean}
 */
export function has_auto_advancing_carousel(layout) {
  const layout_obj = typeof layout === 'number' ? get_layout(layout) : layout;
  if (!layout_obj) return false;
  return layout_obj.auto_advance ?? layout_groups[layout_obj.group]?.auto_advance ?? false;
}

/**
 * Check if a layout overlaps or tilts its entry previews.
 * Every other layout carries a persistent "more actions" button on each preview card, so the
 * affordance is countable and never appears or disappears under the cursor. These layouts
 * cannot: the cards are rotated and sit partly on top of each other, so a button pinned to
 * one card's corner reads as belonging to its neighbour. They keep the single toolbar that
 * follows the hovered card.
 * @param {Object|number} layout - The layout object or layout value/ID
 * @returns {boolean} True if entry previews overlap or are tilted
 */
export function has_overlapping_previews(layout) {
  const layout_obj = typeof layout === 'number' ? get_layout(layout) : layout;
  if (!layout_obj) return false;

  // Layout-specific override takes priority
  if (layout_obj.overlapping_previews !== undefined) {
    return layout_obj.overlapping_previews;
  }

  // Fall back to group default
  const group = layout_groups[layout_obj.group];
  return group?.overlapping_previews || false;
}

/* ═══════════════════════════════════════════════════════════════════════════════════
   WIDTH AND POSITION CAPABILITIES

   Governing rule: never offer a control that will not change anything. A control that
   is present but inert teaches authors the tools are unreliable, so every capability
   below answers one question per layout - does this control do something here?
   ═══════════════════════════════════════════════════════════════════════════════════ */

/** Content width tracks, ordered narrow -> wide */
export const TRACKS = ['inner', 'extended-1', 'extended-2', 'flex', 'full'];

/**
 * How many of the 51 page columns each track spans.
 * Mirrors the track boundaries defined in BlockGrid.svelte; kept here so the editor can
 * reason about slack without reading computed styles.
 */
export const TRACK_SPANS = {
  'inner': 24,        // 14 -> 38
  'extended-1': 32,   // 10 -> 42
  'extended-2': 40,   //  6 -> 46
  'flex': 48,         //  2 -> 50
  'full': 51,         //  1 -> 52
};

/** Where a track sits on the page, when it is narrow enough for that to be visible */
export const TRACK_ALIGNS = ['left', 'center', 'right'];

/**
 * The track matching a pair of page-grid columns, or null if the span is not a track.
 * @param {number} start
 * @param {number} end
 * @returns {string|null}
 */
export function track_from_columns(start, end) {
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  const span = end - start;
  return TRACKS.find(track => TRACK_SPANS[track] === span) ?? null;
}

/**
 * The site's default width. Taken from the dynamic island's own track rather than a
 * separate setting, so blocks line up with the menu by default: the island already
 * stores its width as page-grid columns (10/42, 6/46, 2/50 = extended-1/2/flex).
 * @param {Object} site_settings
 * @returns {string|null}
 */
export function get_site_default_track(site_settings) {
  return track_from_columns(site_settings?.menu_track_start, site_settings?.menu_track_end);
}

/**
 * Positioning a track needs leftover columns to move into. `flex` leaves 3 of 51 and
 * `full` leaves none, so on those two the control would be inert.
 * @param {string} track
 * @returns {boolean}
 */
export function track_has_slack(track) {
  const span = TRACK_SPANS[track];
  return span !== undefined && (50 - 2) - span >= 4;
}

/**
 * Width capability per layout group. Groups listed here have CSS that derives the block's
 * columns from --track-start / --track-end, so any listed track renders correctly.
 *
 * Groups absent from this map keep the width baked into each layout: their CSS hard-codes
 * column spans per variant, so width can still only change by swapping layouts.
 *
 * Single column and cover omit 'full' deliberately - a text column at full width touches
 * the viewport edge at every breakpoint.
 */
const GROUP_TRACKS = {
  'single-column': ['inner', 'extended-1', 'extended-2', 'flex'],
  'grid': ['inner', 'extended-1', 'extended-2', 'flex', 'full'],
  // Cover omits 'full': its text would touch the viewport edge over the image
  'cover': ['inner', 'extended-1', 'extended-2', 'flex'],

  // NOT YET: cover, editorial and bento position their content with hard-coded
  // grid-column values per variant rather than reading --track-start / --track-end, so
  // listing them here would put a control on screen that changes nothing. They join this
  // map as each group's CSS is converted - see the note above the TRACK GEOMETRY block
  // in StoryBlock.svelte for cover's derivation, which is worked out but not yet applied.
};

/**
 * Layouts whose width is load-bearing rather than a free variable. Narrowing a full-bleed
 * layout does not make it narrower, it makes it a different (and usually redundant) layout:
 * "Full + Tiny Image" at inner width is just a worse "Asymmetric".
 */
const FIXED_WIDTH_LAYOUTS = new Set([
  4, 14,    // Full + Small Image      - full-bleed is the design
  5, 28,    // Full + Tiny Image       - ditto
  70, 71,   // Full + Tiny (Alt Order) - ditto
  20, 147,  // Slideshow               - full-bleed crossfade
  144,      // Expanding Carousel      - expands to the full page width
  67,       // Collapsing Cover        - scroll-driven, viewport-sized
  130,      // Cover Grid              - full-bleed
  301, 311, // Full Screen Scroll      - 100vh sections
]);

/**
 * Individual layouts whose CSS has been converted to derive its columns from the track,
 * inside a group that has not been converted wholesale. Listed per layout rather than per
 * group so the rest of the group keeps its fixed width until it is converted too.
 */
const FOUR_TRACKS = ['inner', 'extended-1', 'extended-2', 'flex'];

const CONVERTED_LAYOUTS = new Map([
  // FAQ Accordion: the width now moves the whole container, not the inner columns
  ...[201, 202, 203, 204, 205, 206, 207, 208].map(value => [value, FOUR_TRACKS]),

  // Collage family. Their cards are tilted, and a tilted card overhangs its own box, so
  // none of them offer 'full' - the outermost card would reach under the viewport edge.
  [140, FOUR_TRACKS],                   // 12-column field with min-width:0 cards; narrows cleanly
  [141, FOUR_TRACKS],                   // 2-4 columns, narrows cleanly
  [142, FOUR_TRACKS],                   // 3x3 halo; tight at inner but a legitimate choice
  [143, FOUR_TRACKS],                   // plain centred grid, the cleanest of the four
  // Collage Split: no 'inner' - half of it never fits two pictures across, so it could only
  // ever render stacked, and a width that silently turns the layout into another one is a lie
  [145, ['extended-1', 'extended-2', 'flex']], [146, ['extended-1', 'extended-2', 'flex']],

  // Editorial. 61 stays out: its right edge is 51/42/46 across breakpoints, so no single
  // track reproduces it - and 51 sits inside the page padding, which looks like a bug.
  [22, FOUR_TRACKS],                    // Editorial Grid: two columns either side of the shared split
  [24, FOUR_TRACKS],                    // Definition Layout: constant term column, definition absorbs the rest
  [25, FOUR_TRACKS],                    // Stacked Rows: every field spans the track
  [148, FOUR_TRACKS],                   // Program: text and rows both span the track
  [149, FOUR_TRACKS],                   // Regular dates: text and the weekly plan both span the track
  [60, FOUR_TRACKS],                    // Center Column: only the middle column follows the track
  [62, FOUR_TRACKS], [65, FOUR_TRACKS], // 4-column: middle column scales, sides take the remainder
  [63, FOUR_TRACKS],                    // 4-column asymmetric

  // Bento. Both are diagonals built from the track's own edges, so both take every width.
  [8, FOUR_TRACKS],
  [10, FOUR_TRACKS],

  // The last of the two-column shapes: both columns sit either side of the shared split
  // (--split-left-end / --split-right-start in BlockGrid)
  [2, FOUR_TRACKS], [3, FOUR_TRACKS],
  [12, FOUR_TRACKS], [112, FOUR_TRACKS],
  [121, FOUR_TRACKS], [122, FOUR_TRACKS], // Two Column Carousel: same columns as 12/112
  // Single Column Carousel: unlike its group it does offer 'full'. Only the carousel goes edge
  // to edge there (.track-full keeps the texts inset), which is what a full-bleed slider wants.
  [123, [...FOUR_TRACKS, 'full']], [124, [...FOUR_TRACKS, 'full']],
  [77, FOUR_TRACKS], [177, FOUR_TRACKS],
  [18, FOUR_TRACKS], [110, FOUR_TRACKS],  // Marginale: body column + margin note
  [64, FOUR_TRACKS],                      // Popup Reveal: text centred on the track
  [61, FOUR_TRACKS],                      // 5-Column Symmetric, now its edges are consistent

  [11, FOUR_TRACKS],                    // Gallery Whitecube: narrow + wide column at the centre gutter
  [16, FOUR_TRACKS], [116, FOUR_TRACKS],// Angled Two Column: text 7/24 of the track, images the rest
  [19, FOUR_TRACKS],                    // Progressive Scale: three bands as fractions of the span

  // Carousel Split (two-column). Its width is the text column's inset, and that already keys
  // off the .track-* classes, so block.track drives it as-is - it only needed enabling. Both
  // mirrors get the same four widths; 117-120 were missing handles purely by omission.
  ...[111, 113, 114, 115, 117, 118, 119, 120].map(value => [value, FOUR_TRACKS]),
]);

/**
 * Layouts where the position control would not mean what it says. FAQ divides its track
 * around the container's own midline, so shifting the track off centre would leave its
 * columns landing on gutter cells rather than track boundaries.
 */
const NO_TRACK_ALIGN = new Set([201, 202, 203, 204, 205, 206, 207, 208]);

/**
 * The tracks a layout can be switched between without changing layout.
 * @param {Object|number} layout - The layout object or layout value/ID
 * @returns {string[]|null} Available tracks, or null when width is fixed by the layout
 */
export function get_available_tracks(layout) {
  const layout_obj = typeof layout === 'number' ? get_layout(layout) : layout;
  if (!layout_obj || FIXED_WIDTH_LAYOUTS.has(layout_obj.value)) return null;
  return CONVERTED_LAYOUTS.get(layout_obj.value) ?? GROUP_TRACKS[layout_obj.group] ?? null;
}

/**
 * Whether a block's width can be set independently of its layout
 * @param {Object|number} layout - The layout object or layout value/ID
 * @returns {boolean}
 */
export function supports_independent_track(layout) {
  return get_available_tracks(layout) !== null;
}

/**
 * The track a block actually renders at.
 * Uses the block's own override when the layout supports it and the value is valid,
 * otherwise the track baked into the layout. Keeping the layout's own track as the
 * fallback is what lets every existing layout id keep rendering exactly as before.
 * @param {Object} block - A story block
 * @returns {string|undefined}
 */
export function get_effective_track(block) {
  const layout_obj = get_layout(block?.layout);
  const available = get_available_tracks(layout_obj);
  if (block?.track && available?.includes(block.track)) return block.track;
  return layout_obj?.track;
}

/**
 * Whether the track position control should appear for a block: the layout must derive
 * its columns from the track, and the track must leave room to move.
 * @param {Object} block - A story block
 * @returns {boolean}
 */
export function supports_track_align(block) {
  if (NO_TRACK_ALIGN.has(block?.layout)) return false;
  const layout_obj = get_layout(block?.layout);
  if (!supports_independent_track(layout_obj)) return false;
  // Cover reads position as "which half of the track the text occupies" rather than "where
  // the slack goes", so it stays meaningful even at flex where there is no slack to give.
  if (layout_obj.group === 'cover') return true;
  return track_has_slack(get_effective_track(block));
}

/**
 * Where the block's track sits on the page. Centre unless the block says otherwise, and
 * always centre once the track is too wide for the position to be visible.
 * @param {Object} block - A story block
 * @returns {'left'|'center'|'right'}
 */
export function get_effective_track_align(block) {
  if (!supports_track_align(block)) return 'center';
  if (TRACK_ALIGNS.includes(block?.track_align)) return block.track_align;
  // Fall back to the position baked into the layout, the same way get_effective_track falls
  // back to its track. Cover stores it as `horizontal` ("Cover Top Left" etc.), and without
  // this every existing left/right cover would re-centre the moment it rendered.
  const layout_obj = get_layout(block?.layout);
  return TRACK_ALIGNS.includes(layout_obj?.horizontal) ? layout_obj.horizontal : 'center';
}


export const story_layouts = [
  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  // SINGLE COLUMN LAYOUTS
  // Simple vertical layouts with text and images stacked
  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  { 
    value: 1, 
    group: 'single-column',
    track: 'inner',
    title: 'Single Column',
    search_terms: ['simple', 'basic', 'narrow', 'blog', 'article', 'text', 'centered', 'minimal'],
    fallback_layout: 1,
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  { 
    value: 57, 
    group: 'single-column',
    track: 'extended-1',
    title: 'Single Column',
    search_terms: ['simple', 'basic', 'medium', 'blog', 'article', 'text', 'readable'],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  { 
    value: 58, 
    group: 'single-column',
    track: 'extended-2',
    title: 'Single Column',
    search_terms: ['simple', 'basic', 'wide', 'blog', 'article', 'text', 'spacious'],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  { 
    value: 59, 
    group: 'single-column',
    track: 'flex',
    title: 'Single Column',
    search_terms: ['simple', 'basic', 'full', 'blog', 'article', 'text', 'full-width', 'edge'],
    alternatives: [80, 84],
    image_sizes: '100vw'
  },
  { 
    value: 80, 
    group: 'single-column',
    track: 'flex',
    alignment: 'center',
    title: 'Single Column Center',
    search_terms: ['simple', 'basic', 'full', 'blog', 'article', 'text', 'full-width', 'centered', 'center'],
    alternatives: [59, 84],
    image_sizes: '100vw'
  },
  { 
    value: 84, 
    group: 'single-column',
    track: 'flex',
    alignment: 'center',
    title: 'Single Column Center',
    description: 'Center-aligned, preamble below title',
    search_terms: ['simple', 'basic', 'full', 'blog', 'article', 'text', 'full-width', 'centered', 'center', 'preamble-below'],
    alternatives: [59, 80],
    image_sizes: '100vw'
  },

  // Single Column with the entry previews as a scroll-snap carousel on top and the texts below.
  // No panel height of its own: each slide is as wide as the track and as tall as its image's
  // aspect ratio makes it. 123 splits the texts left/right, 124 centres them.
  {
    value: 123,
    group: 'single-column',
    track: 'extended-1',
    orientation: 'horizontal',
    // Aspect ratio "auto" means each image's own ratio here, not the closest brand ratio -
    // that is the point of this layout. A pinned ratio still wins.
    preserve_preview_aspect_ratio: true,
    inset_carousel: true,
    auto_advance: true,
    title: 'Single Column Carousel',
    description: 'Swipeable carousel on top; preamble and title below left, description and links below right',
    search_terms: ['simple', 'carousel', 'slideshow', 'swipe', 'gallery', 'slider', 'autoplay', 'image-first'],
    alternatives: [124, 121, 144],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  {
    value: 124,
    group: 'single-column',
    track: 'extended-1',
    alignment: 'center',
    orientation: 'horizontal',
    preserve_preview_aspect_ratio: true, // see 123
    inset_carousel: true,
    auto_advance: true,
    title: 'Single Column Carousel Center',
    description: 'Swipeable carousel on top; all texts centred below',
    search_terms: ['simple', 'carousel', 'slideshow', 'swipe', 'gallery', 'slider', 'autoplay', 'image-first', 'centered', 'center'],
    alternatives: [123, 80, 144],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },

  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  // TWO COLUMN LAYOUTS
  // Split layouts with text on one side and images on the other
  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  { 
    value: 2, 
    group: 'two-column',
    track: 'extended-2',
    alignment: 'left',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'Asymmetric',
    search_terms: ['split', 'side-by-side', 'text-left', 'staggered', 'offset', 'alternating', 'story'],
    alternatives: [3, 12],
    constraints: {
      hide_entry_preview_description: true,
    },
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 3, 
    group: 'two-column',
    track: 'extended-2',
    alignment: 'right',
    mirror_of: 2,
    disabled_spacing: ['0'], // Text would touch edge
    title: 'Asymmetric',
    search_terms: ['split', 'side-by-side', 'text-right', 'staggered', 'offset', 'alternating', 'story'],
    alternatives: [2, 12, 77],
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 12, 
    group: 'two-column',
    track: 'extended-2',
    alignment: 'right',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'Equal Two Column',
    search_terms: ['split', 'side-by-side', 'equal', 'balanced', 'symmetric', '50-50'],
    alternatives: [2, 3, 77, 112],
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 112, 
    group: 'two-column',
    track: 'extended-2',
    alignment: 'left',
    mirror_of: 12,
    disabled_spacing: ['0'], // Text would touch edge
    title: 'Equal Two Column',
    search_terms: ['split', 'side-by-side', 'equal', 'balanced', 'symmetric', '50-50'],
    alternatives: [2, 3, 77, 12],
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  // Equal Two Column (12/112) with the entry previews as a scroll-snap carousel. The inset
  // counterpart to Carousel Split: the carousel stays inside the track, padded like the text.
  {
    value: 121,
    group: 'two-column',
    track: 'extended-2',
    alignment: 'left',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'Two Column Carousel',
    orientation: 'horizontal',
    inset_carousel: true,
    auto_advance: true,
    description: 'Text beside a swipeable carousel of entry previews, inset like the text',
    search_terms: ['split', 'side-by-side', 'carousel', 'slideshow', 'swipe', 'gallery', 'inset'],
    alternatives: [12, 112, 113, 118],
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  {
    value: 122,
    group: 'two-column',
    track: 'extended-2',
    alignment: 'right',
    mirror_of: 121,
    disabled_spacing: ['0'], // Text would touch edge
    title: 'Two Column Carousel',
    orientation: 'horizontal',
    inset_carousel: true,
    auto_advance: true,
    description: 'Text beside a swipeable carousel of entry previews, inset like the text',
    search_terms: ['split', 'side-by-side', 'carousel', 'slideshow', 'swipe', 'gallery', 'inset'],
    alternatives: [12, 112, 113, 118],
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 77, 
    group: 'two-column',
    track: 'extended-2',
    alignment: 'right',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'SVG Mask Two Column',
    search_terms: ['mask', 'overlay', 'svg', 'creative', 'artistic', 'shape', 'cutout'],
    alternatives: [2, 3, 12, 177],
    image_sizes: '(min-width: 769px) 50vw, 100vw',
    orientation: 'horizontal',
  },
  { 
    value: 177, 
    group: 'two-column',
    track: 'extended-2',
    alignment: 'left',
    mirror_of: 77,
    disabled_spacing: ['0'], // Text would touch edge
    title: 'SVG Mask Two Column',
    search_terms: ['mask', 'overlay', 'svg', 'creative', 'artistic', 'shape', 'cutout'],
    alternatives: [2, 3, 12, 77],
    image_sizes: '(min-width: 769px) 50vw, 100vw',
    orientation: 'horizontal',
  },
  { 
    value: 4, 
    group: 'two-column',
    track: 'full',
    alignment: 'left',
    default_block_spacing: '0',
    title: 'Full + Small Image',
    search_terms: ['large', 'big', 'hero', 'featured', 'portrait', 'edge-to-edge', 'dramatic'],
    alternatives: [5, 14],
    constraints: {
      aspect_ratio: {
        every_4_starting_1: 'portrait',
        every_4_starting_4: 'portrait',
      },
      even_image_count: true 
      // these types of constraints need to run small calculations themselves, 
      // i.e. the layout should look at the block data and determine if it's suitable or not.
      // for example if(block.entry_previews.length === 3), the layout should not be selected
    },
    image_sizes: '(min-width: 769px) 50vw, 100vw',
  },
  { 
    value: 14, 
    group: 'two-column',
    track: 'full',
    alignment: 'right',
    mirror_of: 4,
    default_block_spacing: '0',
    title: 'Full + Small Image',
    search_terms: ['large', 'big', 'hero', 'featured', 'portrait', 'edge-to-edge', 'dramatic'],
    alternatives: [5, 4],
    constraints: {
      aspect_ratio: {
        every_4_starting_1: 'portrait',
        every_4_starting_4: 'portrait',
      },
      even_image_count: true
    },
    image_sizes: '(min-width: 769px) 50vw, 100vw',
  },
  { 
    value: 5, 
    group: 'two-column',
    track: 'full',
    alignment: 'left',
    default_block_spacing: '0',
    title: 'Full + Tiny Image',
    search_terms: ['large', 'big', 'hero', 'featured', 'accent', 'thumbnail', 'dramatic'],
    alternatives: [28, 70, 71],
    mirror_of: null,
    constraints: {
      max_entry_previews: 2,
      unavailable_animations: ['parallax'],
      preview_texts_available: false,
    },
    image_sizes: '(min-width: 769px) 50vw, 100vw',
  },
  { 
    value: 28, 
    group: 'two-column',
    track: 'full',
    alignment: 'right',
    default_block_spacing: '0',
    mirror_of: 5,
    title: 'Full + Tiny Image',
    search_terms: ['large', 'big', 'hero', 'featured', 'accent', 'thumbnail', 'dramatic'],
    alternatives: [5, 70, 71],
    constraints: {
      max_entry_previews: 2,
      unavailable_animations: ['parallax'],
      preview_texts_available: false,
    },
    image_sizes: '(min-width: 769px) 50vw, 100vw',
  },
  { 
    value: 70, 
    group: 'two-column',
    track: 'full',
    alignment: 'left',
    default_block_spacing: '0',
    title: 'Full + Tiny (Alt Order)',
    search_terms: ['large', 'big', 'hero', 'featured', 'accent', 'preamble-below'],
    alternatives: [5, 28, 71],
    mirror_of: null,
    image_sizes: '(min-width: 769px) 50vw, 100vw',
  },
  { 
    value: 71, 
    group: 'two-column',
    track: 'full',
    alignment: 'right',
    default_block_spacing: '0',
    mirror_of: 70,
    title: 'Full + Tiny (Alt Order)',
    search_terms: ['large', 'big', 'hero', 'featured', 'accent', 'preamble-below'],
    alternatives: [5, 28, 70],
    image_sizes: '(min-width: 769px) 50vw, 100vw',
  },
  { 
    value: 18, 
    group: 'two-column',
    alignment: 'left',
    track: 'flex',
    title: 'Marginale',
    search_terms: ['sidebar', 'margin', 'annotation', 'note', 'aside', 'edge'],
    mirror_of: null,
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 110, 
    group: 'two-column',
    alignment: 'right',
    track: 'flex',
    mirror_of: 18,
    title: 'Marginale',
    search_terms: ['sidebar', 'margin', 'annotation', 'note', 'aside', 'edge'],
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },

  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  // BENTO LAYOUTS
  // Asymmetric grid layouts inspired by bento boxes
  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  { 
    value: 8, 
    group: 'bento',
    track: 'extended-1',
    title: 'Diagonal Bento',
    search_terms: ['asymmetric', 'mixed', 'mosaic', 'creative', 'dynamic', 'diagonal', 'pinterest'],
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 10, 
    group: 'bento',
    track: 'extended-1',
    title: 'Alternating Bento',
    search_terms: ['asymmetric', 'mixed', 'mosaic', 'creative', 'dynamic', 'alternating', 'zigzag'],
    constraints: {
      description_max_characters: 200,
    },
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 11, 
    group: 'special',
    track: 'extended-2',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'Gallery Whitecube',
    search_terms: ['gallery', 'museum', 'whitecube', 'minimal', 'airy', 'whitespace', 'breathing', 'exhibition', 'art', 'clean'],
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 16, 
    group: 'two-column',
    track: 'inner',
    alignment: 'left',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'Angled Two Column',
    search_terms: ['tilted', 'rotated', 'creative', 'playful', 'angular', 'slanted', 'dynamic'],
    alternatives: [116],
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 116, 
    group: 'two-column',
    track: 'inner',
    alignment: 'right',
    mirror_of: 16,
    disabled_spacing: ['0'], // Text would touch edge
    title: 'Angled Two Column',
    search_terms: ['tilted', 'rotated', 'creative', 'playful', 'angular', 'slanted', 'dynamic'],
    alternatives: [16],
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 19, 
    group: 'special',
    track: 'flex',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'Progressive Scale',
    search_terms: ['asymmetric', 'growing', 'scale', 'hierarchy', 'progressive', 'increasing', 'whitespace', 'airy', 'minimal'],
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },

  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  // EDITORIAL LAYOUTS
  // Magazine-style layouts with complex multi-column arrangements
  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  { 
    value: 22,
    group: 'editorial',
    track: 'extended-2',
    default_block_spacing: 'm',
    title: 'Editorial Grid',
    search_terms: ['magazine', 'news', 'article', 'header', 'multi-column', 'complex', 'featured'],
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  {
    value: 24,
    group: 'editorial',
    track: 'inner',
    default_block_spacing: 'xs',
    title: 'Definition Layout',
    search_terms: ['dictionary', 'glossary', 'term', 'explanation', 'sidebar', 'definition'],
    image_sizes: '(min-width: 1025px) 33vw, 100vw'
  },
  {
    value: 25,
    group: 'editorial',
    track: 'flex',
    default_block_spacing: 'm',
    title: 'Stacked Rows',
    search_terms: ['rows', 'horizontal', 'list', 'timeline', 'sequence', 'stacked'],
    image_sizes: '(min-width: 1025px) 33vw, 50vw',
  },
  {
    value: 148,
    group: 'editorial',
    track: 'extended-1',
    default_block_spacing: 'm',
    title: 'Program',
    description: 'Events as rows with a date column, earliest first. Visitors only see events that have not ended yet.',
    search_terms: ['program', 'programme', 'events', 'calendar', 'upcoming', 'agenda', 'dates', 'list', 'schedule'],
    image_sizes: '160px',
  },
  {
    value: 149,
    group: 'editorial',
    track: 'extended-1',
    default_block_spacing: 'm',
    title: 'Regular Dates',
    description: 'The regular groups and meetings from the calendar as a weekly plan, each series once. Fills itself.',
    search_terms: ['regular', 'weekly', 'week', 'plan', 'groups', 'series', 'recurring', 'timetable', 'schedule', 'calendar'],
    image_sizes: '0px',
  },
  {
    value: 60,
    group: 'editorial',
    track: 'inner',
    alignment: 'center',
    title: 'Center Column',
    search_terms: ['magazine', 'centered', 'sticky', 'scroll', 'parallax', 'immersive', 'story'],
    image_sizes: '(min-width: 1025px) 33vw, 100vw'
  },
  {
    value: 61,
    group: 'editorial',
    track: 'flex',
    title: '5-Column Symmetric',
    search_terms: ['magazine', 'newspaper', 'complex', 'multi-column', 'professional', 'dense'],
    image_sizes: '(min-width: 1025px) 20vw, 100vw'
  },
  {
    value: 62,
    group: 'editorial',
    track: 'flex',
    alignment: 'left',
    title: '4-Column Left',
    search_terms: ['magazine', 'marginale', 'annotation', 'sidebar', 'note', 'aside'],
    image_sizes: '(min-width: 1025px) 25vw, 50vw'
  },
  {
    value: 65,
    group: 'editorial',
    track: 'flex',
    alignment: 'center',
    title: '4-Column Center',
    search_terms: ['magazine', 'marginale', 'centered', 'balanced', 'symmetric', 'professional'],
    image_sizes: '(min-width: 1025px) 25vw, 50vw'
  },
  {
    value: 63,
    group: 'editorial',
    track: 'flex',
    title: '4-Column Asymmetric',
    search_terms: ['magazine', 'marginale', 'asymmetric', 'creative', 'offset', 'dynamic'],
    image_sizes: '(min-width: 1025px) 25vw, 50vw',
    orientation: 'horizontal',
  },

  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  // SPECIAL LAYOUTS
  // Unique interactive and animated layouts
  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  {
    value: 20,
    group: 'special',
    track: 'full',
    default_block_spacing: '0',
    fullscreen_previews: true, // Entry previews cover the entire block
    title: 'Slideshow',
    description: 'Full-screen images that crossfade on their own - no swiping, no markers',
    search_terms: ['slideshow', 'carousel', 'slider', 'autoplay', 'crossfade', 'gallery', 'rotating', 'animation'],
    image_sizes: '100vw'
  },
  {
    value: 147,
    group: 'special',
    track: 'full',
    default_block_spacing: '0',
    fullscreen_previews: true,
    title: 'Slideshow Centered',
    description: 'Full-screen crossfading images, texts centred at the bottom edge and revealed after a short delay',
    search_terms: ['slideshow', 'carousel', 'slider', 'autoplay', 'crossfade', 'gallery', 'rotating', 'animation', 'centered', 'center', 'hero', 'landing'],
    image_sizes: '100vw'
  },
  {
    value: 144,
    group: 'special',
    track: 'full',
    orientation: 'horizontal',
    default_block_spacing: '0',
    fullscreen_previews: true,
    auto_advance: true,
    title: 'Expanding Carousel',
    description: 'Images expand on scroll; text overlays on desktop and follows below on mobile',
    search_terms: ['carousel', 'gallery', 'expand', 'scroll', 'hero', 'intro'],
    image_sizes: '100vw'
  },
  {
    value: 64,
    group: 'special',
    track: 'flex',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'Popup Reveal',
    search_terms: ['modal', 'lightbox', 'scroll-triggered', 'reveal', 'animation', 'surprise', 'attention'],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  {
    value: 67,
    group: 'special',
    track: 'full',
    default_block_spacing: '0',
    fullscreen_previews: true, // Entry previews cover the entire block
    title: 'Collapsing Cover',
    search_terms: ['hero', 'scroll', 'shrink', 'collapse', 'animation', 'sticky', 'parallax'],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 130,
    group: 'special',
    track: 'full',
    alignment: 'left',
    default_block_spacing: '0',
    orientation: 'horizontal',
    title: 'Cover Grid',
    description: 'First image as full-bleed background, remaining images as a card grid',
    search_terms: ['cover', 'background', 'menu', 'cards', 'grid', 'overlay', 'poster', 'gradient'],
    constraints: {
      min_entry_previews: 2, // background + at least one card
    },
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },

  // ─── COLLAGE ──────────────────────────────────────────────────────────────────────────────────
  // Deliberately imperfect arrangements: cards are rotated, vertically offset and allowed to
  // overlap. Variation is deterministic (driven by the .every-7-starting-N / .every-8-starting-N
  // classes the preview cards already carry), so a given set of images always composes the same
  // way — no randomness, no layout shift between renders.
  {
    value: 140,
    group: 'special',
    preserve_preview_aspect_ratio: true,
    overlapping_previews: true, // rotated cards - per-card toolbars would land on the neighbour
    track: 'flex',
    disabled_spacing: ['0'], // rotated cards need room to breathe or they clip the block edge
    title: 'Collage',
    description: 'Freeform pinned-up arrangement — rotated, offset and overlapping. Best with 3–8 images.',
    search_terms: ['collage', 'overlap', 'overlapping', 'rotated', 'tilted', 'scattered', 'pinboard', 'playful', 'scrapbook', 'whitespace'],
    constraints: {
      min_entry_previews: 2,
      recommended_max_entry_previews: 8,
    },
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 45vw, 70vw'
  },
  // Collage Split (145/146): 140's composition with the text beside it instead of above it.
  // Splits where 12/112 split (769px). The collage field is a query container: it runs three,
  // two or one across depending on its OWN width (no picture under 180px), so it holds at any
  // track width - inner gets one or two across where flex gets three.
  {
    value: 145,
    group: 'special',
    preserve_preview_aspect_ratio: true,
    overlapping_previews: true, // rotated cards - per-card toolbars would land on the neighbour
    track: 'flex',
    alignment: 'left',
    disabled_spacing: ['0'],
    title: 'Collage Split',
    description: 'Text on one side, a tilted collage on the other — one to three across, whatever fits without shrinking a picture below 180px.',
    search_terms: ['collage', 'split', 'two column', 'side-by-side', 'overlap', 'rotated', 'tilted', 'scattered', 'pinboard', 'playful', 'scrapbook'],
    alternatives: [140, 146],
    constraints: {
      min_entry_previews: 2,
      recommended_max_entry_previews: 8,
    },
    image_sizes: '(min-width: 1025px) 20vw, (min-width: 769px) 25vw, 70vw'
  },
  {
    value: 146,
    group: 'special',
    preserve_preview_aspect_ratio: true,
    overlapping_previews: true,
    track: 'flex',
    alignment: 'right',
    mirror_of: 145,
    disabled_spacing: ['0'],
    title: 'Collage Split',
    description: 'Text on one side, a tilted collage on the other — one to three across, whatever fits without shrinking a picture below 180px.',
    search_terms: ['collage', 'split', 'two column', 'side-by-side', 'overlap', 'rotated', 'tilted', 'scattered', 'pinboard', 'playful', 'scrapbook'],
    alternatives: [140, 145],
    constraints: {
      min_entry_previews: 2,
      recommended_max_entry_previews: 8,
    },
    image_sizes: '(min-width: 1025px) 20vw, (min-width: 769px) 25vw, 70vw'
  },
  {
    value: 141,
    group: 'special',
    preserve_preview_aspect_ratio: true,
    overlapping_previews: true, // rotated cards - per-card toolbars would land on the neighbour
    track: 'extended-2',
    disabled_spacing: ['0'],
    title: 'Collage Grid',
    description: 'Flat grid with no categories — cards rotated and unevenly offset. Scales to many items.',
    search_terms: ['collage', 'grid', 'rotated', 'tilted', 'flat', 'index', 'directory', 'playful', 'scrapbook', 'masonry'],
    image_sizes: '(min-width: 1281px) 25vw, (min-width: 769px) 33vw, 50vw'
  },
  {
    value: 142,
    group: 'special',
    preserve_preview_aspect_ratio: true,
    overlapping_previews: true, // rotated cards - per-card toolbars would land on the neighbour
    track: 'flex',
    disabled_spacing: ['0'],
    title: 'Centred Halo',
    description: 'Text in the middle of a three-by-three field, up to 8 images ringed clockwise around it; any further images continue below in the same columns.',
    search_terms: ['collage', 'centred', 'centered', 'halo', 'ring', 'scattered', 'around', 'hero', 'fullscreen', 'playful', 'statement'],
    constraints: {
      recommended_max_entry_previews: 8, // the ring has eight positions; the rest flow underneath
    },
    image_sizes: '(min-width: 1281px) 28vw, 48vw'
  },
  {
    value: 143,
    group: 'special',
    preserve_preview_aspect_ratio: true,
    overlapping_previews: true, // rotated cards - per-card toolbars would land on the neighbour
    track: 'extended-1',
    disabled_spacing: ['0'],
    title: 'Centred Statement',
    description: 'Text centred on a full screen, images in rows of four beneath it.',
    search_terms: ['centred', 'centered', 'statement', 'hero', 'fullscreen', 'grid', 'four', 'columns', 'rows'],
    image_sizes: '(min-width: 1281px) 25vw, (min-width: 769px) 33vw, 50vw'
  },

  // FAQ Accordion layouts - text on one side, FAQ list on the other
  // Left-aligned variants (text left, FAQ right)
  {
    value: 201,
    group: 'special',
    track: 'inner',
    alignment: 'left',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'FAQ Accordion',
    search_terms: ['faq', 'accordion', 'collapsible', 'questions', 'answers', 'expandable', 'details'],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  {
    value: 202,
    group: 'special',
    track: 'extended-1',
    alignment: 'left',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'FAQ Accordion',
    search_terms: ['faq', 'accordion', 'collapsible', 'questions', 'answers', 'expandable', 'details'],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  {
    value: 203,
    group: 'special',
    track: 'extended-2',
    alignment: 'left',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'FAQ Accordion',
    search_terms: ['faq', 'accordion', 'collapsible', 'questions', 'answers', 'expandable', 'details'],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  {
    value: 204,
    group: 'special',
    track: 'flex',
    alignment: 'left',
    disabled_spacing: ['0'], // Text would touch edge
    title: 'FAQ Accordion',
    search_terms: ['faq', 'accordion', 'collapsible', 'questions', 'answers', 'expandable', 'details'],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  // Right-aligned variants (text right, FAQ left) - mirrors of above
  {
    value: 205,
    group: 'special',
    track: 'inner',
    alignment: 'right',
    mirror_of: 201,
    disabled_spacing: ['0'], // Text would touch edge
    title: 'FAQ Accordion',
    search_terms: ['faq', 'accordion', 'collapsible', 'questions', 'answers', 'expandable', 'details'],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  {
    value: 206,
    group: 'special',
    track: 'extended-1',
    alignment: 'right',
    mirror_of: 202,
    disabled_spacing: ['0'], // Text would touch edge
    title: 'FAQ Accordion',
    search_terms: ['faq', 'accordion', 'collapsible', 'questions', 'answers', 'expandable', 'details'],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  {
    value: 207,
    group: 'special',
    track: 'extended-2',
    alignment: 'right',
    mirror_of: 203,
    disabled_spacing: ['0'], // Text would touch edge
    title: 'FAQ Accordion',
    search_terms: ['faq', 'accordion', 'collapsible', 'questions', 'answers', 'expandable', 'details'],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  {
    value: 208,
    group: 'special',
    track: 'flex',
    alignment: 'right',
    mirror_of: 204,
    disabled_spacing: ['0'], // Text would touch edge
    title: 'FAQ Accordion',
    search_terms: ['faq', 'accordion', 'collapsible', 'questions', 'answers', 'expandable', 'details'],
    image_sizes: '(min-width: 1025px) 66vw, 100vw'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  // GRID LAYOUTS
  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  
  // UP TO 6 COLUMNS
  {
    value: 69,
    group: 'grid',
    track: 'extended-2',
    alignment: 'center',
    columns: 6,
    title: 'Grid 6 Columns',
    description: 'Center, extended-2 track',
    constraints: {
      entry_preview_title_max_characters: 20,
    },
    image_sizes: '(min-width: 1281px) 17vw, (min-width: 769px) 25vw, 33vw'
  },

  // UP TO 5 COLUMNS
  { 
    value: 30, 
    group: 'grid',
    track: 'inner',
    alignment: 'center',
    columns: 5,
    title: 'Grid 5 Columns',
    description: 'Center, inner track',
    constraints: {
      entry_preview_title_max_characters: 20,
    },
    image_sizes: '(min-width: 1281px) 20vw, (min-width: 769px) 33vw, 50vw'
  },
  { 
    value: 44, 
    group: 'grid',
    track: 'extended-1',
    alignment: 'center',
    columns: 5,
    title: 'Grid 5 Columns',
    description: 'Center, extended-1 track',
    constraints: {
      entry_preview_title_max_characters: 20,
    },
    image_sizes: '(min-width: 1281px) 20vw, (min-width: 769px) 33vw, 50vw'
  },
  { 
    value: 6, 
    group: 'grid',
    track: 'extended-2',
    alignment: 'center',
    columns: 5,
    title: 'Grid 5 Columns',
    description: 'Center, extended-2 track',
    constraints: {
      entry_preview_title_max_characters: 20,
    },
    image_sizes: '(min-width: 1281px) 20vw, (min-width: 769px) 33vw, 50vw'
  },
  { 
    value: 32, 
    group: 'grid',
    track: 'flex',
    alignment: 'center',
    columns: 5,
    title: 'Grid 5 Columns',
    description: 'Center, flex track',
    constraints: {
      entry_preview_title_max_characters: 20,
    },
    image_sizes: '(min-width: 1281px) 20vw, (min-width: 769px) 33vw, 50vw'
  },
  { 
    value: 29, 
    group: 'grid',
    track: 'inner',
    alignment: 'center',
    columns: 5,
    title: 'Grid 5 Columns',
    description: 'Center, inner track (alt)',
    constraints: {
      entry_preview_title_max_characters: 20,
    },
    image_sizes: '(min-width: 1281px) 20vw, (min-width: 769px) 33vw, 50vw'
  },
  { 
    value: 45, 
    group: 'grid',
    track: 'extended-1',
    alignment: 'left',
    columns: 5,
    title: 'Grid 5 Columns',
    description: 'Left, extended-1 track',
    constraints: {
      entry_preview_title_max_characters: 20,
    },
    image_sizes: '(min-width: 1281px) 20vw, (min-width: 769px) 33vw, 50vw'
  },
  { 
    value: 23, 
    group: 'grid',
    track: 'extended-2',
    alignment: 'left',
    columns: 5,
    title: 'Grid 5 Columns',
    description: 'Left, extended-2 track',
    constraints: {
      entry_preview_title_max_characters: 20,
    },
    image_sizes: '(min-width: 1281px) 20vw, (min-width: 769px) 33vw, 50vw'
  },
  { 
    value: 31, 
    group: 'grid',
    track: 'flex',
    alignment: 'left',
    columns: 5,
    title: 'Grid 5 Columns',
    description: 'Left, flex track',
    constraints: {
      entry_preview_title_max_characters: 20,
    },
    image_sizes: '(min-width: 1281px) 20vw, (min-width: 769px) 33vw, 50vw'
  },
  
  // UP TO 4 COLUMNS
  { 
    value: 37, 
    group: 'grid',
    track: 'inner',
    alignment: 'left',
    columns: 4,
    title: 'Grid 4 Columns',
    description: 'Left, inner track',
    image_sizes: '(min-width: 1281px) 25vw, (min-width: 769px) 50vw, 50vw'
  },
  { 
    value: 46, 
    group: 'grid',
    track: 'extended-1',
    alignment: 'left',
    columns: 4,
    title: 'Grid 4 Columns',
    description: 'Left, extended-1 track',
    image_sizes: '(min-width: 1281px) 25vw, (min-width: 769px) 50vw, 50vw'
  },
  { 
    value: 38, 
    group: 'grid',
    track: 'extended-2',
    alignment: 'left',
    columns: 4,
    title: 'Grid 4 Columns',
    description: 'Left, extended-2 track',
    image_sizes: '(min-width: 1281px) 25vw, (min-width: 769px) 50vw, 50vw'
  },
  { 
    value: 39, 
    group: 'grid',
    track: 'flex',
    alignment: 'left',
    columns: 4,
    title: 'Grid 4 Columns',
    description: 'Left, flex track',
    image_sizes: '(min-width: 1281px) 25vw, (min-width: 769px) 50vw, 50vw'
  },
  { 
    value: 68, 
    group: 'grid',
    track: 'flex',
    alignment: 'left',
    columns: 4,
    title: 'Grid 4 Columns',
    // Had its own hover image swap; every one-picture layout swaps now, so it equals 39
    description: 'Left, flex track',
    image_sizes: '(min-width: 1281px) 25vw, (min-width: 769px) 50vw, 50vw'
  },
  { 
    value: 40, 
    group: 'grid',
    track: 'inner',
    alignment: 'center',
    columns: 4,
    title: 'Grid 4 Columns',
    description: 'Center, inner track',
    image_sizes: '(min-width: 1281px) 25vw, (min-width: 769px) 50vw, 50vw'
  },
  { 
    value: 47, 
    group: 'grid',
    track: 'extended-1',
    alignment: 'center',
    columns: 4,
    title: 'Grid 4 Columns',
    description: 'Center, extended-1 track',
    image_sizes: '(min-width: 1281px) 25vw, (min-width: 769px) 50vw, 50vw'
  },
  { 
    value: 41, 
    group: 'grid',
    track: 'extended-2',
    alignment: 'center',
    columns: 4,
    title: 'Grid 4 Columns',
    description: 'Center, extended-2 track',
    image_sizes: '(min-width: 1281px) 25vw, (min-width: 769px) 50vw, 50vw'
  },
  { 
    value: 42, 
    group: 'grid',
    track: 'flex',
    alignment: 'center',
    columns: 4,
    title: 'Grid 4 Columns',
    description: 'Center, flex track',
    image_sizes: '(min-width: 1281px) 25vw, (min-width: 769px) 50vw, 50vw'
  },
  
  // UP TO 3 COLUMNS
  { 
    value: 15, 
    group: 'grid',
    track: 'inner',
    alignment: 'left',
    columns: 3,
    title: 'Grid 3 Columns',
    description: 'Left, inner track',
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 26, 
    group: 'grid',
    track: 'extended-1',
    alignment: 'left',
    columns: 3,
    title: 'Grid 3 Columns',
    description: 'Left, extended-1 track',
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 43, 
    group: 'grid',
    track: 'extended-2',
    alignment: 'left',
    columns: 3,
    title: 'Grid 3 Columns',
    description: 'Left, extended-2 track',
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  {
    value: 75,
    group: 'grid',
    track: 'extended-2',
    alignment: 'left',
    columns: 3,
    title: 'Grid 3 Columns',
    description: 'Left, extended-2 - titles above image',
    constraints: {
      entry_preview_title_max_characters: 20,
    },
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 33, 
    group: 'grid',
    track: 'flex',
    alignment: 'left',
    columns: 3,
    title: 'Grid 3 Columns',
    description: 'Left, flex track',
    alternatives: [76],
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  {
    value: 76,
    group: 'grid',
    track: 'flex',
    alignment: 'left',
    columns: 3,
    title: 'Grid 3 Columns',
    description: 'Left, flex - links below previews',
    alternatives: [33],
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw',
  },
  { 
    value: 34, 
    group: 'grid',
    track: 'inner',
    alignment: 'center',
    columns: 3,
    title: 'Grid 3 Columns',
    description: 'Center, inner track',
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 48, 
    group: 'grid',
    track: 'extended-1',
    alignment: 'center',
    columns: 3,
    title: 'Grid 3 Columns',
    description: 'Center, extended-1 track',
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 35, 
    group: 'grid',
    track: 'extended-2',
    alignment: 'center',
    columns: 3,
    title: 'Grid 3 Columns',
    description: 'Center, extended-2 track',
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 36, 
    group: 'grid',
    track: 'flex',
    alignment: 'center',
    columns: 3,
    title: 'Grid 3 Columns',
    description: 'Center, flex track',
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 66, 
    group: 'grid',
    track: 'flex',
    alignment: 'center',
    columns: 3,
    title: 'Grid 3 Columns',
    description: 'Center, flex - description on hover',
    image_sizes: '(min-width: 1281px) 33vw, (min-width: 769px) 50vw, 100vw'
  },

  // UP TO 2 COLUMNS
  { 
    value: 49, 
    group: 'grid',
    track: 'inner',
    alignment: 'left',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Left, inner track',
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 50, 
    group: 'grid',
    track: 'extended-1',
    alignment: 'left',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Left, extended-1 track',
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 51, 
    group: 'grid',
    track: 'extended-2',
    alignment: 'left',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Left, extended-2 track',
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 52, 
    group: 'grid',
    track: 'flex',
    alignment: 'left',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Left, flex track',
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 53, 
    group: 'grid',
    track: 'full',
    alignment: 'left',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Left, full-screen',
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 54, 
    group: 'grid',
    track: 'inner',
    alignment: 'center',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Center, inner track',
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 7, 
    group: 'grid',
    track: 'extended-1',
    alignment: 'center',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Center, extended-1 track',
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 55, 
    group: 'grid',
    track: 'extended-2',
    alignment: 'center',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Center, extended-2 track',
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 9, 
    group: 'grid',
    track: 'flex',
    alignment: 'center',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Center, flex track',
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 27, 
    group: 'grid',
    track: 'full',
    alignment: 'center',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Center, full-screen',
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 21, 
    group: 'grid',
    track: 'inner',
    alignment: 'center',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Center, inner - sticky description left',
    constraints: {
      description_max_characters: 200,
    },
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },
  { 
    value: 56, 
    group: 'grid',
    track: 'inner',
    alignment: 'center',
    columns: 2,
    title: 'Grid 2 Columns',
    description: 'Center, inner - sticky description right',
    constraints: {
      description_max_characters: 200,
    },
    image_sizes: '(min-width: 769px) 50vw, 100vw'
  },

  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  // COVER / HERO LAYOUTS
  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  
  // TOP ALIGNED - Center tracks
  {
    value: 100,
    group: 'cover',
    orientation: 'horizontal',
    track: 'flex',
    vertical: 'top',
    horizontal: 'center',
    title: 'Cover Top Center',
    description: 'Text top, flex track width',
    alternatives: [101, 102, 103, 104, 105, 106, 107, 108, 109],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 101,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-2',
    vertical: 'top',
    horizontal: 'center',
    title: 'Cover Top Center',
    description: 'Text top, extended-2 track',
    alternatives: [100, 102, 103, 104, 105, 106, 107, 108, 109],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 102,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-1',
    vertical: 'top',
    horizontal: 'center',
    title: 'Cover Top Center',
    description: 'Text top, extended-1 track',
    alternatives: [100, 101, 103, 104, 105, 106, 107, 108, 109],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 103,
    group: 'cover',
    orientation: 'horizontal',
    track: 'inner',
    vertical: 'top',
    horizontal: 'center',
    title: 'Cover Top Center',
    description: 'Text top, inner track',
    alternatives: [100, 101, 102, 104, 105, 106, 107, 108, 109],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  // TOP ALIGNED - Left tracks
  {
    value: 104,
    group: 'cover',
    orientation: 'horizontal',
    track: 'flex',
    vertical: 'top',
    horizontal: 'left',
    title: 'Cover Top Left',
    description: 'Text top-left, flex track',
    alternatives: [100, 101, 102, 103, 105, 106, 107, 108, 109],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 105,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-1',
    vertical: 'top',
    horizontal: 'left',
    title: 'Cover Top Left',
    description: 'Text top-left, extended track',
    alternatives: [100, 101, 102, 103, 104, 106, 107, 108, 109],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 106,
    group: 'cover',
    orientation: 'horizontal',
    track: 'inner',
    vertical: 'top',
    horizontal: 'left',
    title: 'Cover Top Left',
    description: 'Text top-left, inner track',
    alternatives: [100, 101, 102, 103, 104, 105, 107, 108, 109],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  // TOP ALIGNED - Right tracks
  {
    value: 107,
    group: 'cover',
    orientation: 'horizontal',
    track: 'flex',
    vertical: 'top',
    horizontal: 'right',
    title: 'Cover Top Right',
    description: 'Text top-right, flex track',
    alternatives: [100, 101, 102, 103, 104, 105, 106, 108, 109],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 108,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-1',
    vertical: 'top',
    horizontal: 'right',
    title: 'Cover Top Right',
    description: 'Text top-right, extended track',
    alternatives: [100, 101, 102, 103, 104, 105, 106, 107, 109],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 109,
    group: 'cover',
    orientation: 'horizontal',
    track: 'inner',
    vertical: 'top',
    horizontal: 'right',
    title: 'Cover Top Right',
    description: 'Text top-right, inner track',
    alternatives: [100, 101, 102, 103, 104, 105, 106, 107, 108],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  
  // CENTER ALIGNED - Center tracks
  {
    value: 72,
    group: 'cover',
    orientation: 'horizontal',
    track: 'flex',
    vertical: 'center',
    horizontal: 'center',
    title: 'Cover Center',
    description: 'Text centered, flex track',
    alternatives: [73, 74, 81, 82, 83, 85, 86, 88, 89],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 81,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-2',
    vertical: 'center',
    horizontal: 'center',
    title: 'Cover Center',
    description: 'Text centered, extended-2 track',
    alternatives: [72, 73, 74, 82, 83, 85, 86, 88, 89],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 82,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-1',
    vertical: 'center',
    horizontal: 'center',
    title: 'Cover Center',
    description: 'Text centered, extended-1 track',
    alternatives: [72, 73, 74, 81, 83, 85, 86, 88, 89],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 83,
    group: 'cover',
    orientation: 'horizontal',
    track: 'inner',
    vertical: 'center',
    horizontal: 'center',
    title: 'Cover Center',
    description: 'Text centered, inner track',
    alternatives: [72, 73, 74, 81, 82, 85, 86, 88, 89],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  // CENTER ALIGNED - Left tracks
  {
    value: 74,
    group: 'cover',
    orientation: 'horizontal',
    track: 'flex',
    vertical: 'center',
    horizontal: 'left',
    title: 'Cover Center Left',
    description: 'Text center-left, flex track',
    alternatives: [72, 73, 81, 82, 83, 85, 86, 88, 89],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 85,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-1',
    vertical: 'center',
    horizontal: 'left',
    title: 'Cover Center Left',
    description: 'Text center-left, extended track',
    alternatives: [72, 73, 74, 81, 82, 83, 86, 88, 89],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 86,
    group: 'cover',
    orientation: 'horizontal',
    track: 'inner',
    vertical: 'center',
    horizontal: 'left',
    title: 'Cover Center Left',
    description: 'Text center-left, inner track',
    alternatives: [72, 73, 74, 81, 82, 83, 85, 88, 89],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  // CENTER ALIGNED - Right tracks
  {
    value: 73,
    group: 'cover',
    orientation: 'horizontal',
    track: 'flex',
    vertical: 'center',
    horizontal: 'right',
    title: 'Cover Center Right',
    description: 'Text center-right, flex track',
    alternatives: [72, 74, 81, 82, 83, 85, 86, 88, 89],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 88,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-1',
    vertical: 'center',
    horizontal: 'right',
    title: 'Cover Center Right',
    description: 'Text center-right, extended track',
    alternatives: [72, 73, 74, 81, 82, 83, 85, 86, 89],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 89,
    group: 'cover',
    orientation: 'horizontal',
    track: 'inner',
    vertical: 'center',
    horizontal: 'right',
    title: 'Cover Center Right',
    description: 'Text center-right, inner track',
    alternatives: [72, 73, 74, 81, 82, 83, 85, 86, 88],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  
  // BOTTOM ALIGNED - Center tracks
  {
    value: 90,
    group: 'cover',
    orientation: 'horizontal',
    track: 'flex',
    vertical: 'bottom',
    horizontal: 'center',
    title: 'Cover Bottom Center',
    description: 'Text bottom, flex track',
    alternatives: [78, 79, 91, 92, 93, 95, 96, 98, 99],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 91,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-2',
    vertical: 'bottom',
    horizontal: 'center',
    title: 'Cover Bottom Center',
    description: 'Text bottom, extended-2 track',
    alternatives: [78, 79, 90, 92, 93, 95, 96, 98, 99],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 92,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-1',
    vertical: 'bottom',
    horizontal: 'center',
    title: 'Cover Bottom Center',
    description: 'Text bottom, extended-1 track',
    alternatives: [78, 79, 90, 91, 93, 95, 96, 98, 99],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 93,
    group: 'cover',
    orientation: 'horizontal',
    track: 'inner',
    vertical: 'bottom',
    horizontal: 'center',
    title: 'Cover Bottom Center',
    description: 'Text bottom, inner track',
    alternatives: [78, 79, 90, 91, 92, 95, 96, 98, 99],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  // BOTTOM ALIGNED - Left tracks
  {
    value: 79,
    group: 'cover',
    orientation: 'horizontal',
    track: 'flex',
    vertical: 'bottom',
    horizontal: 'left',
    title: 'Cover Bottom Left',
    description: 'Text bottom-left, flex track',
    alternatives: [78, 90, 91, 92, 93, 95, 96, 98, 99],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 95,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-1',
    vertical: 'bottom',
    horizontal: 'left',
    title: 'Cover Bottom Left',
    description: 'Text bottom-left, extended track',
    alternatives: [78, 79, 90, 91, 92, 93, 96, 98, 99],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 96,
    group: 'cover',
    orientation: 'horizontal',
    track: 'inner',
    vertical: 'bottom',
    horizontal: 'left',
    title: 'Cover Bottom Left',
    description: 'Text bottom-left, inner track',
    alternatives: [78, 79, 90, 91, 92, 93, 95, 98, 99],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  // BOTTOM ALIGNED - Right tracks
  {
    value: 78,
    group: 'cover',
    orientation: 'horizontal',
    track: 'flex',
    vertical: 'bottom',
    horizontal: 'right',
    title: 'Cover Bottom Right',
    description: 'Text bottom-right, flex track',
    alternatives: [79, 90, 91, 92, 93, 95, 96, 98, 99],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 98,
    group: 'cover',
    orientation: 'horizontal',
    track: 'extended-1',
    vertical: 'bottom',
    horizontal: 'right',
    title: 'Cover Bottom Right',
    description: 'Text bottom-right, extended track',
    alternatives: [78, 79, 90, 91, 92, 93, 95, 96, 99],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },
  {
    value: 99,
    group: 'cover',
    orientation: 'horizontal',
    track: 'inner',
    vertical: 'bottom',
    horizontal: 'right',
    title: 'Cover Bottom Right',
    description: 'Text bottom-right, inner track',
    alternatives: [78, 79, 90, 91, 92, 93, 95, 96, 98],
    image_sizes: '(min-width: 1025px) 100vw, 100vw'
  },

  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  // TWO-COLUMN CAROUSEL SPLIT LAYOUTS (111, 113-115, 117-120)
  // Split-screen layouts with text on one side and carousel on the other
  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  
  // Left variants (text left, carousel right)
  { 
    value: 111, 
    group: 'two-column', 
    orientation: 'horizontal', 
    track: 'flex', 
    alignment: 'left', 
    vertical: 'spread', 
    default_block_spacing: '0', 
    title: 'Carousel Split', 
    search_terms: ['split', 'carousel', 'hero'], 
    image_sizes: '(min-width: 1025px) 50vw, 100vw', 
    fullscreen_previews: true,
  },
  { 
    value: 113, 
    group: 'two-column', 
    orientation: 'horizontal', 
    track: 'extended-2', 
    alignment: 'left', 
    vertical: 'spread', 
    default_block_spacing: '0', 
    title: 'Carousel Split', 
    search_terms: ['split', 'carousel', 'hero'], 
    image_sizes: '(min-width: 1025px) 50vw, 100vw',
    fullscreen_previews: true,
  },
  { 
    value: 114, 
    group: 'two-column', 
    orientation: 'horizontal', 
    track: 'extended-1', 
    alignment: 'left', 
    vertical: 'spread', 
    default_block_spacing: '0', 
    title: 'Carousel Split', 
    search_terms: ['split', 'carousel', 'hero'], 
    image_sizes: '(min-width: 1025px) 50vw, 100vw', 
    fullscreen_previews: true,
  },
  { 
    value: 115, 
    group: 'two-column', 
    orientation: 'horizontal', 
    track: 'inner', 
    alignment: 'left', 
    vertical: 'spread', 
    default_block_spacing: '0', 
    title: 'Carousel Split', 
    search_terms: ['split', 'carousel', 'hero'], 
    image_sizes: '(min-width: 1025px) 50vw, 100vw', 
    fullscreen_previews: true,
  },
  // Right variants (text right, carousel left)
  { 
    value: 117, 
    group: 'two-column', 
    orientation: 'horizontal', 
    track: 'flex', 
    alignment: 'right', 
    vertical: 'spread', 
    mirror_of: 111, 
    default_block_spacing: '0', 
    title: 'Carousel Split', 
    search_terms: ['split', 'carousel', 'hero'], 
    image_sizes: '(min-width: 1025px) 50vw, 100vw', 
    fullscreen_previews: true,
  },
  { 
    value: 118, 
    group: 'two-column', 
    orientation: 'horizontal', 
    track: 'extended-2', 
    alignment: 'right', 
    vertical: 'spread', 
    mirror_of: 113, 
    default_block_spacing: '0', 
    title: 'Carousel Split', 
    search_terms: ['split', 'carousel', 'hero'], 
    image_sizes: '(min-width: 1025px) 50vw, 100vw', 
    fullscreen_previews: true,
  },
  { 
    value: 119, 
    group: 'two-column', 
    orientation: 'horizontal', 
    track: 'extended-1', 
    alignment: 'right', 
    vertical: 'spread', 
    mirror_of: 114, 
    default_block_spacing: '0', 
    title: 'Carousel Split', 
    search_terms: ['split', 'carousel', 'hero'], 
    image_sizes: '(min-width: 1025px) 50vw, 100vw', 
    fullscreen_previews: true,
  },
  {
    value: 120,
    group: 'two-column',
    orientation: 'horizontal',
    track: 'inner',
    alignment: 'right',
    vertical: 'spread',
    mirror_of: 115,
    default_block_spacing: '0',
    title: 'Carousel Split',
    search_terms: ['split', 'carousel', 'hero'],
    image_sizes: '(min-width: 1025px) 50vw, 100vw',
    fullscreen_previews: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════════════════════════
  // FULL-SCREEN SCROLL LAYOUTS
  // Full-viewport scroll-driven sections with cross-fade and pagination
  // ═══════════════════════════════════════════════════════════════════════════════════════════════

  // Full-screen scroll (image left on wide+short split)
  {
    value: 301,
    group: 'special',
    track: 'full',
    columns: 1,
    alignment: 'left',
    default_block_spacing: '0',
    fullscreen_previews: true,
    title: 'Full Screen Scroll',
    description: 'Full-screen scroll with cross-fade, split image-left on wide viewports',
    search_terms: ['fullscreen', 'scroll', 'crossfade', 'team', 'profiles', 'people', 'showcase', 'about'],
    alternatives: [311],
    image_sizes: '100vw',
  },
  // Full-screen scroll - mirror (image right on wide+short split)
  {
    value: 311,
    group: 'special',
    track: 'full',
    columns: 1,
    alignment: 'right',
    mirror_of: 301,
    default_block_spacing: '0',
    fullscreen_previews: true,
    title: 'Full Screen Scroll',
    description: 'Full-screen scroll with cross-fade, split image-right on wide viewports',
    search_terms: ['fullscreen', 'scroll', 'crossfade', 'team', 'profiles', 'people', 'showcase', 'about'],
    alternatives: [301],
    image_sizes: '100vw',
  },
];
